import React, { useCallback, useEffect, useState } from 'react';
import { BorderAlignmentTypes } from '@bynder-studio/render-core';
import { IconBorderWidth } from '@bynder/icons';
import { Dropdown, Form, Input } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { useInputBlur } from 'packages/hooks/useInputBlur';
import editorAutocorrect from 'packages/pages/editor/EditorAutocorrectRules';
import { BorderAlignButton } from './index.styled';

type Props = {
    isDisabled: boolean;
    defaultBorderWidth: number;
    defaultBorderAlignment: BorderAlignmentTypes;
    onBorderWidthChange?: (borderWidth: number) => void;
    onBorderAlignmentChange?: (borderAlignment: BorderAlignmentTypes) => void;
};

const BorderWeightAlignment = ({
    isDisabled,
    defaultBorderWidth,
    defaultBorderAlignment,
    onBorderWidthChange,
    onBorderAlignmentChange,
}: Props) => {
    const { translate } = useTranslate();
    const { onFocus, unsubscribeListener } = useInputBlur();
    const [selectedWeight, setSelectedBorderWeight] = useState(defaultBorderWidth || 0);
    const [selectedAlignment, setSelectedBorderAlignment] = useState(defaultBorderAlignment);

    const borderAlignmentTitles = {
        [BorderAlignmentTypes.INSIDE]: translate('editor.sidebar.shots.shape.border_align.inside'),
        [BorderAlignmentTypes.MIDDLE]: translate('editor.sidebar.shots.shape.border_align.center'),
        [BorderAlignmentTypes.OUTSIDE]: translate('editor.sidebar.shots.shape.border_align.outside'),
    };

    const applyBorderWeight = (val: number) => {
        const correctWeight = editorAutocorrect('element_border_weight', val);

        setSelectedBorderWeight(correctWeight);
        onBorderWidthChange(correctWeight);
    };

    const onBorderWeightBlur = () => {
        unsubscribeListener();
        applyBorderWeight(Number(selectedWeight));
    };

    const onBorderWeightKeyDown = (event) => {
        switch (event.key) {
            case 'Enter':
                applyBorderWeight(Number(selectedWeight));
                break;
            case 'ArrowUp':
            case 'ArrowDown': {
                event.preventDefault();
                const numberToAdd = event.key === 'ArrowDown' ? -1 : 1;
                const factor = event.shiftKey ? 10 : 1;
                applyBorderWeight(Number(selectedWeight) + numberToAdd * factor);
                break;
            }
        }
    };

    const setBorderAlignment = useCallback<(alignment: BorderAlignmentTypes) => void>(
        (alignment) => {
            setSelectedBorderAlignment(alignment);
            onBorderAlignmentChange(alignment);
        },
        [onBorderAlignmentChange],
    );

    useEffect(() => {
        setSelectedBorderWeight(defaultBorderWidth);
        setSelectedBorderAlignment(defaultBorderAlignment);
    }, [defaultBorderWidth, defaultBorderAlignment]);

    return (
        <Form.Group>
            <Form.Label>{translate('editor.sidebar.shots.shape.border_weight.label')}</Form.Label>
            <Form.Row>
                <Input
                    name={translate('editor.sidebar.shots.shape.border_weight.label')}
                    aria-label={translate('editor.sidebar.shots.shape.border_weight.label')}
                    type="number"
                    value={selectedWeight?.toString()}
                    onFocus={onFocus}
                    onBlur={onBorderWeightBlur}
                    onChange={(newVal) => setSelectedBorderWeight(Number(newVal))}
                    onKeyDown={onBorderWeightKeyDown}
                    isDisabled={isDisabled}
                    icon={<IconBorderWidth />}
                    suffix="px"
                    data-testid="shots_shape_border__weight"
                />
                <Dropdown
                    trigger={({ isOpen, ...triggerProps }) => (
                        <BorderAlignButton
                            rightIcon={<Dropdown.Arrow />}
                            isDisabled={isDisabled}
                            isPressed={isOpen}
                            isFullWidth
                            {...triggerProps}
                        >
                            {borderAlignmentTitles[selectedAlignment]}
                        </BorderAlignButton>
                    )}
                    position="bottom"
                    data-testid="shots_shape_border__alignment"
                >
                    <Dropdown.Item
                        isChecked={selectedAlignment.toUpperCase() === BorderAlignmentTypes.INSIDE}
                        onClick={() => setBorderAlignment(BorderAlignmentTypes.INSIDE)}
                    >
                        {borderAlignmentTitles[BorderAlignmentTypes.INSIDE]}
                    </Dropdown.Item>
                    <Dropdown.Item
                        isChecked={selectedAlignment.toUpperCase() === BorderAlignmentTypes.MIDDLE}
                        onClick={() => setBorderAlignment(BorderAlignmentTypes.MIDDLE)}
                    >
                        {borderAlignmentTitles[BorderAlignmentTypes.MIDDLE]}
                    </Dropdown.Item>
                    <Dropdown.Item
                        isChecked={selectedAlignment.toUpperCase() === BorderAlignmentTypes.OUTSIDE}
                        onClick={() => setBorderAlignment(BorderAlignmentTypes.OUTSIDE)}
                    >
                        {borderAlignmentTitles[BorderAlignmentTypes.OUTSIDE]}
                    </Dropdown.Item>
                </Dropdown>
            </Form.Row>
        </Form.Group>
    );
};

export default BorderWeightAlignment;
